import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { getTableByQR, createOrder, getTableOrders } from '../api/restaurant';
import { getMenuCategories } from '../api/api';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ShoppingCart, Plus, Minus, Send, User, Clock, ChefHat, Check, CreditCard, ArrowLeft, Flame, Droplets, UtensilsCrossed, Loader2, Wine, BellRing, Star, MessageSquare, X, Beer } from 'lucide-react';
import api from '../api/axios';

const PRINT_STATUS_LABELS = {
  en_attente_impression: { label: 'Impression en cours...', color: 'text-amber-600', done: false },
  en_impression: { label: 'Impression en cours...', color: 'text-amber-600', done: false },
  imprimee: { label: 'Commande imprimee', color: 'text-emerald-700', done: true },
};

const OrderPrintStatus = ({ order }) => {
  const printCfg = PRINT_STATUS_LABELS[order.print_status];
  return (
    <div className="flex flex-wrap items-center gap-2 mb-2 pb-2 border-b border-gray-100" data-testid={`print-status-${order.id}`}>
      <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
        <Check size={12} /> Commande envoyee
      </span>
      {printCfg && (
        <>
          <span className="text-gray-300">→</span>
          <span className={`flex items-center gap-1 text-[11px] font-semibold ${printCfg.color}`}>
            {printCfg.done ? <Check size={12} /> : <Loader2 size={12} className="animate-spin" />}
            {printCfg.label}
          </span>
        </>
      )}
    </div>
  );
};

const STATUS_CONFIG = {
  'En attente': { color: 'bg-amber-50 text-amber-700 border-amber-300', icon: Clock },
  'En préparation': { color: 'bg-yellow-50 text-yellow-700 border-yellow-300', icon: ChefHat },
  'Prête': { color: 'bg-emerald-50 text-emerald-700 border-emerald-300', icon: Check },
  'Servie': { color: 'bg-sky-50 text-sky-700 border-sky-300', icon: CreditCard },
  'Payée': { color: 'bg-gray-50 text-gray-400 border-gray-200', icon: Check },
};

const CustomerOrder = () => {
  const { qrCode } = useParams();
  const [table, setTable] = useState(null);
  const [menuCategories, setMenuCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [notes, setNotes] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [view, setView] = useState('menu');
  const [myOrders, setMyOrders] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // AI suggestions
  const [showOptions, setShowOptions] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedCuisson, setSelectedCuisson] = useState(null);
  const [selectedSauces, setSelectedSauces] = useState([]);
  const [selectedAccomp, setSelectedAccomp] = useState([]);
  // Wine color picker
  const [showWinePicker, setShowWinePicker] = useState(false);
  const [pendingWineItem, setPendingWineItem] = useState(null);

  const [menuTab, setMenuTab] = useState('food'); // 'food' or 'drinks'
  const [billRequested, setBillRequested] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [drinksWithFood, setDrinksWithFood] = useState(false);
  const [showDrinksPrompt, setShowDrinksPrompt] = useState(false);

  const foodCategories = menuCategories.filter(c => !c.isDrink);
  const drinkCategories = menuCategories.filter(c => c.isDrink);
  const visibleCategories = menuTab === 'food' ? foodCategories : drinkCategories;

  useEffect(() => {
    const hasFood = cart.some(item => item.type !== 'boisson');
    const hasUndecidedDrink = cart.some(
      item => item.type === 'boisson' && !item.drink_timing_selected
    );
    if (hasFood && hasUndecidedDrink && !showOptions && !showWinePicker) {
      setShowDrinksPrompt(true);
    }
  }, [cart, showOptions, showWinePicker]);

  const handleMenuTabChange = (tab) => {
    setMenuTab(tab);
    const cats = tab === 'food' ? foodCategories : drinkCategories;
    if (cats.length > 0 && !cats.find(c => c.id === selectedCategory)) {
      setSelectedCategory(cats[0].id);
    }
  };
  const [requestingBill, setRequestingBill] = useState(false);
  const [waiterCalled, setWaiterCalled] = useState(false);
  const [callingWaiter, setCallingWaiter] = useState(false);

  // Review states
  const [showReview, setShowReview] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewName, setReviewName] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [notifiedPrinted, setNotifiedPrinted] = useState({});

  useEffect(() => {
    myOrders.forEach((order) => {
      if (order.print_status === 'imprimee' && !notifiedPrinted[order.id]) {
        setNotifiedPrinted((prev) => ({ ...prev, [order.id]: true }));
        toast({ title: 'Commande imprimee !', description: 'Votre commande a ete imprimee en caisse.' });
      }
    });
  }, [myOrders, notifiedPrinted]);

  const handleCallWaiter = async () => {
    if (waiterCalled || callingWaiter || !table) return;
    setCallingWaiter(true);
    try {
      await api.post(`/tables/${table.table_number}/call-waiter`);
      setWaiterCalled(true);
      toast({ title: 'Serveur appele !', description: 'Un serveur arrive a votre table.' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally { setCallingWaiter(false); }
  };

  const handleRequestBill = async () => {
    if (billRequested || requestingBill) return;
    setRequestingBill(true);
    try {
      await api.post(`/tables/${table.table_number}/request-bill`);
      setBillRequested(true);
      toast({ title: 'Addition demandee !', description: 'Votre serveur arrive.' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally { setRequestingBill(false); }
  };

  const handleSubmitReview = async () => {
    if (reviewRating === 0) return;
    setSubmittingReview(true);
    try {
      await api.post('/reviews', {
        rating: reviewRating,
        comment: reviewComment,
        name: reviewName || customerName,
        table_number: table?.table_number,
      });
      setReviewSubmitted(true);
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
    finally { setSubmittingReview(false); }
  };

  useEffect(() => { fetchData(); }, [qrCode]);

  const fetchData = async () => {
    try {
      const [tableData, menuData, drinksData] = await Promise.all([
        getTableByQR(qrCode),
        getMenuCategories(),
        api.get('/menu/drinks/all').then(r => r.data).catch(() => [])
      ]);
      setTable(tableData);
      // Merge food + drinks into one list, mark drinks
      const drinkCats = drinksData.map(c => ({ ...c, isDrink: true }));
      setMenuCategories([...menuData, ...drinkCats]);
      if (menuData.length > 0) setSelectedCategory(menuData[0].id);
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de charger le menu', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const fetchMyOrders = useCallback(async () => {
    if (!table) return;
    try {
      const [orders, tableStatus] = await Promise.all([
        getTableOrders(table.table_number),
        api.get(`/tables/number/${table.table_number}`).then(r => r.data).catch(() => null)
      ]);
      setMyOrders(orders.filter(o => o.status !== 'Payée' && o.status !== 'Payee' && o.status !== 'Annulee' && o.status !== 'Annulée'));
      // Sync waiter/bill state from server
      if (tableStatus) {
        if (!tableStatus.waiter_called && waiterCalled) {
          setWaiterCalled(false);
        }
        if (!tableStatus.bill_requested && billRequested) {
          setBillRequested(false);
        }
      }
    } catch (error) { console.error('Error:', error); }
  }, [table, waiterCalled, billRequested]);

  useEffect(() => {
    if (view === 'tracking' && table) {
      fetchMyOrders();
      const interval = setInterval(fetchMyOrders, 5000);
      return () => clearInterval(interval);
    }
  }, [view, table, fetchMyOrders]);

  const isWineGlass = (item, cat) => {
    const n = (item.name || '').toLowerCase();
    const cid = (cat?.id || '').toLowerCase();
    return n.includes('verre') || (cid.startsWith('vins') && (n.includes('pichet') || n.includes('verre')));
  };

  const handleAddItem = async (item) => {
    // For drinks, add directly without AI suggestions
    const currentCat = menuCategories.find(c => c.id === selectedCategory);
    if (currentCat?.isDrink) {
      // Wine glass color picker
      if (isWineGlass(item, currentCat)) {
        setPendingWineItem(item);
        setShowWinePicker(true);
        return;
      }
      addToCartDirect(item, null, null, []);
      // Show drinks-with-food prompt if cart has food items and prompt hasn't been shown yet
      const hasFood = cart.some(i => i.type !== 'boisson');
      if (hasFood && !drinksWithFood && !showDrinksPrompt) {
        setShowDrinksPrompt(true);
      }
      return;
    }

    setCurrentItem(item);
    setSelectedCuisson(null);
    setSelectedSauces([]);
    setSelectedAccomp([]);
    setLoadingSuggestions(true);
    setShowOptions(true);

    try {
      const categoryName = currentCat?.name || '';
      const res = await api.post('/ai/suggestions', { item_name: item.name, category: categoryName, item_id: item.id });
      const data = res.data;
      if (data.needs_cuisson || data.needs_sauce || data.needs_accompagnement) {
        setSuggestions(data);
      } else {
        setShowOptions(false);
        addToCartDirect(item, null, null, []);
      }
    } catch (error) {
      setShowOptions(false);
      addToCartDirect(item, null, null, []);
    } finally { setLoadingSuggestions(false); }
  };

  const addToCartDirect = (item, cuisson, sauces, accomp, serveWithFood = false) => {
    const currentCat = menuCategories.find(c => c.id === selectedCategory);
    const itemType = currentCat?.isDrink ? 'boisson' : 'plat';
    const catName = currentCat?.name || '';
    const accompNames = (accomp || []).map(a => typeof a === 'object' ? a.name : a);
    const includedAccompaniments = suggestions?.included_accompaniments ?? 2;
    const extraAccompanimentFee = suggestions?.extra_accompaniment_fee ?? 5;
    const pricedAccompSupplement = (accomp || []).reduce((s, a) => s + (typeof a === 'object' && a.prix ? a.prix : 0), 0);
    const extraAccompSupplement = Math.max(0, accompNames.length - includedAccompaniments) * extraAccompanimentFee;
    const accompSupplement = pricedAccompSupplement + extraAccompSupplement;
    const basePrice = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
    const selectedSauceList = sauces || [];
    const sauceSupplement = selectedSauceList.length > 1 ? 5 : 0;
    const finalPrice = (basePrice + accompSupplement + sauceSupplement).toFixed(2);
    setCart(prev => {
      const key = `${item.id}-${cuisson || ''}-${selectedSauceList.join(',')}-${accompNames.join(',')}-${serveWithFood ? 'avec-plats' : 'direct'}`;
      const existing = prev.find(i => i._key === key);
      if (existing) {
        return prev.map(i => i._key === key ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, {
        _key: key, menu_item_id: item.id, name: item.name, price: finalPrice,
        quantity: 1, cuisson, sauces: selectedSauceList,
        sauce: selectedSauceList.join(', ') || null,
        base_price: basePrice.toFixed(2),
        accompagnement_supplement: accompSupplement,
        extra_accompaniment_supplement: extraAccompSupplement,
        sauce_supplement: sauceSupplement,
        accompagnements: accompNames, notes: '',
        type: itemType, category_name: catName, serve_with_food: serveWithFood,
        drink_timing_selected: itemType !== 'boisson'
      }];
    });
  };

  const confirmOptions = () => {
    if (currentItem) {
      addToCartDirect(currentItem, selectedCuisson, selectedSauces, selectedAccomp);
    }
    setShowOptions(false);
    setSuggestions(null);
  };

  const updateQuantity = (key, delta) => {
    setCart(prev => prev.map(item => {
      if (item._key === key) {
        const nq = item.quantity + delta;
        return nq > 0 ? { ...item, quantity: nq } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const toggleDrinkTiming = (key) => {
    const item = cart.find(entry => entry._key === key);
    setDrinkTiming(key, !item?.serve_with_food);
  };

  const setDrinkTiming = (key, serveWithFood) => {
    setCart(prev => {
      const next = prev.map(item => item._key === key
        ? { ...item, serve_with_food: serveWithFood, drink_timing_selected: true }
        : item);
      const drinks = next.filter(item => item.type === 'boisson');
      setDrinksWithFood(drinks.length > 0 && drinks.every(item => item.serve_with_food));
      return next;
    });
  };

  const calculateTotal = () => cart.reduce((s, i) => s + parseFloat(i.price) * i.quantity, 0).toFixed(2);

  const handleSubmitOrder = async () => {
    const hasFood = cart.some(item => item.type !== 'boisson');
    const hasUndecidedDrink = cart.some(
      item => item.type === 'boisson' && !item.drink_timing_selected
    );
    if (hasFood && hasUndecidedDrink) {
      setShowDrinksPrompt(true);
      toast({
        title: 'Choix obligatoire',
        description: 'Indiquez si les boissons doivent être servies directement ou avec les plats.',
        variant: 'destructive',
      });
      return;
    }
    if (cart.length === 0) { toast({ title: 'Panier vide', variant: 'destructive' }); return; }
    if (!customerName.trim()) { toast({ title: 'Nom requis', description: 'Entrez votre nom pour commander', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      const items = cart.map(({ _key, ...rest }) => rest);
      await createOrder({ table_number: table.table_number, table_id: qrCode, customer_name: customerName.trim(), items, total: calculateTotal(), notes, drinks_with_food: cart.some(item => item.type === 'boisson' && item.serve_with_food) });
      toast({ title: 'Commande envoyee !', description: 'Votre commande est transmise. Impression en caisse en cours...' });
      setCart([]);
      setNotes('');
      setView('tracking');
    } catch (error) {
      toast({ title: 'Erreur', description: "Impossible d'envoyer la commande", variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a0f08]">
      <div className="text-xl text-amber-100 font-semibold animate-pulse">Chargement du menu...</div>
    </div>
  );

  if (!table) return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a0f08]">
      <Card className="max-w-md border-red-500"><CardContent className="pt-6"><p className="text-center text-xl text-red-500 font-bold">Table non trouvee ou inactive</p></CardContent></Card>
    </div>
  );

  const currentCategory = menuCategories.find(c => c.id === selectedCategory);
  const grandTotal = myOrders.reduce((s, o) => s + parseFloat(o.total || 0), 0).toFixed(2);

  return (
    <div className={`min-h-[100dvh] bg-[#faf8f5] overflow-x-hidden ${cart.length > 0 ? 'pb-[calc(270px+env(safe-area-inset-bottom))] sm:pb-[230px]' : 'pb-[env(safe-area-inset-bottom)]'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2c1810] to-[#4a2c1a] text-white px-2 sm:px-4 py-2 sm:py-4 pt-[calc(0.5rem+env(safe-area-inset-top))] sticky top-0 z-40 shadow-xl">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
            <img src="/logo-taverne.png" alt="La Taverne" className="h-8 sm:h-10 shrink-0" data-testid="restaurant-name" />
            <p className="text-[10px] sm:text-xs text-amber-200/80 truncate">{table.name || `Table ${table.table_number}`}</p>
          </div>
          <div className="flex gap-1 shrink-0">
            <button data-testid="tab-menu" onClick={() => setView('menu')}
              className={`px-2.5 py-1.5 sm:px-3 rounded-full text-[10px] sm:text-xs font-semibold transition-all ${view === 'menu' ? 'bg-white/20 text-white' : 'bg-white/10 text-amber-100 hover:bg-white/20'}`}>
              Menu
            </button>
            <button data-testid="tab-tracking" onClick={() => { setView('tracking'); fetchMyOrders(); }}
              className={`px-2.5 py-1.5 sm:px-3 rounded-full text-[10px] sm:text-xs font-semibold transition-all relative ${view === 'tracking' ? 'bg-white/20 text-white' : 'bg-white/10 text-amber-100 hover:bg-white/20'}`}>
              Suivi
              {myOrders.length > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{myOrders.length}</span>}
            </button>
          </div>
        </div>
      </div>

      {view === 'menu' ? (
        <>
          {/* Category Tabs - filtered by food/drinks */}
          <div className="bg-white border-b sticky top-[48px] sm:top-[72px] z-30 shadow-sm">
            <div className="max-w-5xl mx-auto border-b px-2 py-1.5 flex gap-1.5">
              <button data-testid="tab-food" onClick={() => handleMenuTabChange('food')}
                className={`flex-1 py-1.5 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 ${menuTab === 'food' ? 'bg-[#2c1810] text-white' : 'bg-gray-100 text-gray-600'}`}>
                <UtensilsCrossed size={12} /> Plats
              </button>
              <button data-testid="tab-drinks" onClick={() => handleMenuTabChange('drinks')}
                className={`flex-1 py-1.5 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 ${menuTab === 'drinks' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                <Wine size={12} /> Boissons
              </button>
            </div>
            <div className="overflow-x-auto overscroll-x-contain">
            <div className="flex gap-1.5 px-2 sm:px-3 py-2 max-w-5xl mx-auto">
              {visibleCategories.map(cat => (
                <button key={cat.id} onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-full whitespace-nowrap transition-all text-xs font-medium flex items-center gap-1 ${selectedCategory === cat.id ? (cat.isDrink ? 'bg-amber-600 text-white shadow' : 'bg-[#2c1810] text-white shadow') : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {cat.isDrink && <Wine size={12} />}
                  {cat.name.replace('Nos ', '')}
                </button>
              ))}
            </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="max-w-5xl mx-auto px-2 sm:px-3 py-3 sm:py-4">
            {/* Disclaimer Banner */}
            <div className="hidden sm:flex mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 items-start gap-2" data-testid="menu-disclaimer-banner">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-500 mt-0.5 shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <p className="text-[11px] text-amber-700 leading-relaxed">
                Les images sont donnees a titre indicatif et peuvent ne pas representer exactement le plat ou la boisson servi(e).
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {currentCategory?.items.map(item => (
                <div key={item.id} className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all overflow-hidden flex" data-testid={`menu-item-${item.id}`}>
                    {item.image && (
                      <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded-lg m-2 cursor-pointer shrink-0" onClick={() => setZoomedImage(item.image)} data-testid={`item-image-${item.id}`} />
                    )}
                  <div className="p-2.5 flex-1 min-w-0 flex flex-col justify-center">
                    <h3 className="font-semibold text-sm text-gray-800 mb-0.5 truncate">{item.name}</h3>
                    {item.description && <p className="text-xs text-gray-500 mb-1 line-clamp-1">{item.description}</p>}
                    <div className="flex items-center justify-between">
                      <span className="text-base font-bold text-[#8b4513]">{item.price}&euro;</span>
                      <Button onClick={() => handleAddItem(item)} size="sm" className="bg-[#2c1810] hover:bg-[#4a2c1a] text-white h-7 px-2.5 text-xs" data-testid={`add-to-cart-${item.id}`}>
                        <Plus size={12} className="mr-0.5" /> Ajouter
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cart - Fixed Bottom */}
          {cart.length > 0 && (
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#8b4513] shadow-2xl z-50 max-h-[58dvh] overflow-y-auto pb-[env(safe-area-inset-bottom)]">
              <div className="max-w-5xl mx-auto p-2 sm:p-3">
                <div className="max-h-[24dvh] sm:max-h-28 overflow-y-auto mb-2 space-y-1">
                  {cart.map(item => (
                    <div key={item._key} className="flex flex-wrap items-center justify-between gap-1 text-xs bg-amber-50 rounded-lg px-2 py-1.5">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium truncate block">{item.name}</span>
                        {(item.cuisson || item.sauce || item.accompagnements?.length > 0) && (
                          <span className="text-[10px] text-gray-500">
                            {[item.cuisson, item.sauce, ...(item.accompagnements || [])].filter(Boolean).join(' | ')}
                          </span>
                        )}
                        {item.type === 'boisson' && (
                          <button onClick={() => toggleDrinkTiming(item._key)}
                            className={`mt-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded border ${
                              !item.drink_timing_selected
                                ? 'bg-red-50 text-red-700 border-red-300'
                                : item.serve_with_food
                                  ? 'bg-amber-100 text-amber-800 border-amber-300'
                                  : 'bg-blue-50 text-blue-700 border-blue-200'
                            }`}>
                            {!item.drink_timing_selected ? 'Choix du service requis' : item.serve_with_food ? 'Avec les plats' : 'Service direct'}
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-auto shrink-0">
                        <button onClick={() => updateQuantity(item._key, -1)} className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"><Minus size={12} /></button>
                        <span className="font-bold w-4 text-center text-xs">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item._key, 1)} className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"><Plus size={12} /></button>
                        <span className="text-gray-600 w-12 text-right text-xs">{(parseFloat(item.price) * item.quantity).toFixed(2)}&euro;</span>
                      </div>
                    </div>
                  ))}
                </div>
                <Textarea data-testid="order-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Instructions speciales..." rows={1} className="mb-2 text-xs h-8 min-h-0" />
                {/* Name + Submit */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2 flex-1">
                    <User size={14} className="text-amber-600 shrink-0" />
                    <Input data-testid="customer-name-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Votre prenom *" className="border-0 bg-transparent h-9 text-sm p-0 focus-visible:ring-0" />
                  </div>
                  <Button data-testid="submit-order-btn" onClick={handleSubmitOrder} disabled={submitting || !customerName.trim()}
                    className="bg-[#2c1810] hover:bg-[#4a2c1a] text-white h-9 px-4 text-sm font-semibold disabled:opacity-40 shrink-0">
                    <Send size={14} className="mr-1.5" />
                    {submitting ? '...' : `${calculateTotal()}\u20AC`}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* TRACKING VIEW */
        <div className="max-w-2xl mx-auto px-3 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Suivi de commande</h2>
              <p className="text-xs text-gray-500">Table {table.table_number}</p>
            </div>
            <Button data-testid="back-to-menu-btn" onClick={() => setView('menu')} variant="outline" size="sm" className="text-xs h-8">
              <ArrowLeft size={14} className="mr-1" /> Menu
            </Button>
          </div>

          {/* Call Waiter Button */}
          <button onClick={handleCallWaiter} disabled={waiterCalled || callingWaiter}
            className="w-full mb-4 transition-all" data-testid="call-waiter-btn">
            <Card className={`border-2 ${waiterCalled ? 'border-green-500 bg-green-50' : 'border-amber-400 bg-amber-50 hover:bg-amber-100 active:scale-[0.98]'} transition-all`}>
              <CardContent className="py-3 px-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BellRing size={22} className={waiterCalled ? 'text-green-600' : 'text-amber-600'} />
                  <div>
                    <p className={`text-sm font-bold ${waiterCalled ? 'text-green-700' : 'text-amber-800'}`}>
                      {waiterCalled ? 'Serveur appele !' : 'Appeler un serveur'}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {waiterCalled ? 'Un serveur arrive a votre table' : 'Besoin d\'aide ? Appelez votre serveuse'}
                    </p>
                  </div>
                </div>
                {callingWaiter && <Loader2 size={20} className="animate-spin text-amber-500" />}
                {waiterCalled && <Check size={20} className="text-green-600" />}
              </CardContent>
            </Card>
          </button>

          {/* Review Button / Form */}
          <div className="mb-4" data-testid="review-section">
            {reviewSubmitted ? (
              <Card className="border-2 border-emerald-400 bg-emerald-50">
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <Check size={22} className="text-emerald-600" />
                  <div>
                    <p className="text-sm font-bold text-emerald-700">Merci pour votre avis !</p>
                    <div className="flex gap-0.5 mt-0.5">
                      {[1,2,3,4,5].map(s => <Star key={s} size={12} className={s <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : showReview ? (
              <Card className="border-2 border-amber-300 bg-white">
                <CardContent className="py-4 px-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-gray-800">Donnez votre avis</p>
                    <button onClick={() => setShowReview(false)} className="text-gray-400 text-xs hover:underline">Fermer</button>
                  </div>
                  <div className="flex justify-center gap-2" data-testid="review-stars">
                    {[1,2,3,4,5].map(s => (
                      <button key={s} onClick={() => setReviewRating(s)} className="transition-transform hover:scale-110" data-testid={`review-star-${s}`}>
                        <Star size={32} className={`transition-colors ${s <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                  {reviewRating > 0 && <p className="text-center text-xs text-amber-600 font-medium">{['','Decevant','Moyen','Bien','Tres bien','Excellent'][reviewRating]}</p>}
                  <textarea
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Votre commentaire (optionnel)..."
                    rows={2}
                    maxLength={500}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-amber-400 resize-none"
                    data-testid="review-comment-input"
                  />
                  <input
                    type="text"
                    value={reviewName}
                    onChange={(e) => setReviewName(e.target.value)}
                    placeholder={customerName ? `Prenom (${customerName})` : 'Votre prenom (optionnel)'}
                    maxLength={100}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-amber-400"
                    data-testid="review-name-input"
                  />
                  <Button onClick={handleSubmitReview} disabled={submittingReview || reviewRating === 0}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xs h-9" data-testid="submit-review-btn">
                    {submittingReview ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} className="mr-1" /> Envoyer</>}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <button onClick={() => setShowReview(true)} className="w-full text-left transition-all" data-testid="open-review-btn">
                <Card className="border-2 border-stone-200 bg-white hover:border-amber-300 hover:bg-amber-50/50 active:scale-[0.98] transition-all">
                  <CardContent className="py-3 px-4 flex items-center gap-3">
                    <MessageSquare size={20} className="text-amber-500" />
                    <div>
                      <p className="text-sm font-bold text-gray-700">Donnez votre avis</p>
                      <p className="text-[10px] text-gray-400">Comment trouvez-vous votre experience ?</p>
                    </div>
                    <Star size={18} className="text-amber-400 ml-auto" />
                  </CardContent>
                </Card>
              </button>
            )}
          </div>

          {myOrders.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingCart size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">Aucune commande en cours</p>
              <Button onClick={() => setView('menu')} className="mt-3 bg-[#2c1810] hover:bg-[#4a2c1a] text-sm">Commander</Button>
            </div>
          ) : (
            <>
              {myOrders.map(order => {
                const config = STATUS_CONFIG[order.status] || STATUS_CONFIG['En attente'];
                const StatusIcon = config.icon;
                return (
                  <Card key={order.id} className="mb-3 border shadow-sm" data-testid={`order-card-${order.id}`}>
                    <CardHeader className="pb-1.5 pt-3 px-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-gray-700">{order.customer_name}</span>
                          <span className="text-[10px] text-gray-400">Table {order.table_number}</span>
                          <span className="text-[10px] text-gray-400">{new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Cayenne' })}</span>
                        </div>
                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${config.color}`}>
                          <StatusIcon size={12} />{order.status}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="px-3 pb-3">
                      <OrderPrintStatus order={order} />
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                          <div>
                            <span className="font-medium">{item.quantity}x {item.name}</span>
                            {(item.cuisson || item.sauce || item.accompagnements?.length > 0) && (
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                {[item.cuisson && `Cuisson: ${item.cuisson}`, item.sauce && `Sauce: ${item.sauce}`, item.accompagnements?.length > 0 && `Accomp: ${item.accompagnements.join(', ')}`].filter(Boolean).join(' | ')}
                              </p>
                            )}
                          </div>
                          <span className="text-gray-500 shrink-0">{(parseFloat(item.price) * item.quantity).toFixed(2)}&euro;</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-bold text-sm mt-2 pt-1.5 border-t"><span>Sous-total</span><span className="text-[#8b4513]">{order.total}&euro;</span></div>
                    </CardContent>
                  </Card>
                );
              })}
              {/* Addition - Clickable only if at least one order is Servie */}
              {myOrders.some(o => o.status === 'Servie') ? (
              <button onClick={handleRequestBill} disabled={billRequested || requestingBill}
                className="w-full text-left transition-all" data-testid="request-bill-btn">
                <Card className={`border-2 ${billRequested ? 'border-green-600 bg-green-700' : 'border-[#2c1810] bg-[#2c1810] hover:bg-[#3d2219] active:scale-[0.98]'} text-white transition-all`}>
                  <CardContent className="py-4 px-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-amber-200/70">Addition - Table {table.table_number}</p>
                        <p className="text-2xl font-bold">{grandTotal}&euro;</p>
                      </div>
                      {billRequested ? (
                        <Check size={28} className="text-green-200" />
                      ) : requestingBill ? (
                        <Loader2 size={28} className="text-amber-200/50 animate-spin" />
                      ) : (
                        <CreditCard size={28} className="text-amber-200/50" />
                      )}
                    </div>
                    <p className={`text-xs mt-2 font-semibold ${billRequested ? 'text-green-200' : 'text-amber-200/80'}`}>
                      {billRequested ? 'Addition demandee - Votre serveur arrive !' : 'Appuyez pour demander l\'addition'}
                    </p>
                  </CardContent>
                </Card>
              </button>
              ) : (
              <Card className="border-2 border-gray-300 bg-gray-100 text-gray-400" data-testid="bill-total-card">
                <CardContent className="py-4 px-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-400">Addition - Table {table.table_number}</p>
                      <p className="text-2xl font-bold">{grandTotal}&euro;</p>
                    </div>
                    <Clock size={28} className="text-gray-300" />
                  </div>
                  <p className="text-xs mt-2 text-gray-400">En attente du service de vos plats</p>
                </CardContent>
              </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* AI Options Dialog */}
      <Dialog open={showOptions} onOpenChange={setShowOptions}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-sm max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">{currentItem?.name}</DialogTitle>
          </DialogHeader>
          {loadingSuggestions ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-amber-600" size={28} /></div>
          ) : suggestions ? (
            <div className="space-y-4 mt-2">
              {suggestions.needs_cuisson && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5"><Flame size={12} className="text-orange-500" /> Cuisson</label>
                  <div className="flex flex-wrap gap-1">
                    {suggestions.cuisson_options.map(opt => (
                      <button key={opt} onClick={() => setSelectedCuisson(opt)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${selectedCuisson === opt ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {suggestions.needs_sauce && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5"><Droplets size={12} className="text-amber-600" /> Sauce</label>
                  <div className="flex flex-wrap gap-1">
                    {suggestions.sauce_options.map(opt => (
                      <button key={opt} onClick={() => setSelectedSauces(prev => prev.includes(opt) ? prev.filter(sauce => sauce !== opt) : [...prev, opt])}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${selectedSauces.includes(opt) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">Une sauce est incluse. Vous pouvez en choisir plusieurs.</p>
                  {selectedSauces.length > 1 && (
                    <div className="mt-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" role="alert">
                      Plusieurs sauces sélectionnées : supplément de 5,00 € ajouté au prix du plat.
                    </div>
                  )}
                </div>
              )}
              {suggestions.needs_accompagnement && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5"><UtensilsCrossed size={12} className="text-green-600" /> Accompagnement</label>
                  <div className="flex flex-wrap gap-1">
                    {suggestions.accompagnement_options.map(opt => {
                      const optName = typeof opt === 'object' ? opt.name : opt;
                      const optPrix = typeof opt === 'object' ? opt.prix : 0;
                      const isSelected = selectedAccomp.some(a => (typeof a === 'object' ? a.name : a) === optName);
                      return (
                        <button key={optName} onClick={() => setSelectedAccomp(prev => {
                          const has = prev.some(a => (typeof a === 'object' ? a.name : a) === optName);
                          return has ? prev.filter(a => (typeof a === 'object' ? a.name : a) !== optName) : [...prev, optPrix > 0 ? { name: optName, prix: optPrix } : optName];
                        })}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${isSelected ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:border-green-300'}`}>
                          {optName}{optPrix > 0 && <span className="ml-1 text-[9px]">+{optPrix.toFixed(2)}&euro;</span>}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">
                    {suggestions.included_accompaniments ?? 2} accompagnements inclus.
                  </p>
                  {selectedAccomp.length > (suggestions.included_accompaniments ?? 2) && (
                    <div className="mt-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" role="alert">
                      {selectedAccomp.length - (suggestions.included_accompaniments ?? 2)} accompagnement(s) supplémentaire(s) :
                      +{((selectedAccomp.length - (suggestions.included_accompaniments ?? 2)) * (suggestions.extra_accompaniment_fee ?? 5)).toFixed(2)} €.
                    </div>
                  )}
                </div>
              )}
              <Button onClick={confirmOptions} className="w-full bg-[#2c1810] hover:bg-[#4a2c1a] text-white mt-2" data-testid="confirm-options-btn">
                <Plus size={14} className="mr-1.5" /> Ajouter au panier
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Drinks with food prompt dialog */}
      <Dialog open={showDrinksPrompt} onOpenChange={open => {
        if (open || !cart.some(item => item.type === 'boisson' && !item.drink_timing_selected)) {
          setShowDrinksPrompt(open);
        }
      }}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-sm max-h-[90dvh] overflow-y-auto p-4 sm:p-6" data-testid="drinks-with-food-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <Beer size={20} className="text-amber-500" />
              Quand servir les boissons ?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 mt-1">
            Choisissez le service pour chaque boisson. Ce choix est obligatoire.
          </p>
          <div className="space-y-2 mt-3">
            {cart.filter(item => item.type === 'boisson').map(item => (
              <div key={item._key} className="rounded-lg border bg-gray-50 p-2">
                <p className="text-xs font-bold text-gray-800 mb-1.5">{item.quantity}x {item.name}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <button onClick={() => setDrinkTiming(item._key, false)}
                    className={`rounded-md border px-2 py-1.5 text-[11px] font-bold ${item.drink_timing_selected && !item.serve_with_food ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200'}`}>
                    Service direct
                  </button>
                  <button onClick={() => setDrinkTiming(item._key, true)}
                    className={`rounded-md border px-2 py-1.5 text-[11px] font-bold ${item.drink_timing_selected && item.serve_with_food ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200'}`}>
                    Avec les plats
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" onClick={() => {
              setCart(prev => prev.map(item => item.type === 'boisson' ? { ...item, serve_with_food: false, drink_timing_selected: true } : item));
              setDrinksWithFood(false);
            }}>Tout en direct</Button>
            <Button variant="outline" onClick={() => {
              setCart(prev => prev.map(item => item.type === 'boisson' ? { ...item, serve_with_food: true, drink_timing_selected: true } : item));
              setDrinksWithFood(true);
            }}>Tout avec plats</Button>
          </div>
          <Button className="w-full mt-2 bg-[#2c1810] text-white"
            disabled={cart.some(item => item.type === 'boisson' && !item.drink_timing_selected)}
            onClick={() => setShowDrinksPrompt(false)}>
            Valider le service des boissons
          </Button>
        </DialogContent>
      </Dialog>

      {/* Image Zoom Lightbox */}
      {zoomedImage && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4" onClick={() => setZoomedImage(null)} data-testid="image-lightbox">
          <button className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors" onClick={() => setZoomedImage(null)} data-testid="close-lightbox">
            <X size={24} />
          </button>
          <img src={zoomedImage} alt="" className="max-w-full max-h-[80vh] rounded-xl object-contain shadow-2xl" />

      {/* Wine Color Picker Dialog */}
      <Dialog open={showWinePicker} onOpenChange={o => { if (!o) { setShowWinePicker(false); setPendingWineItem(null); } }}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-xs max-h-[90dvh] overflow-y-auto p-4 sm:p-6" data-testid="wine-color-picker">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2"><Wine size={18} className="text-[#8b4513]" /> {pendingWineItem?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mb-3">Choisissez la couleur :</p>
          <div className="flex flex-col gap-2">
            {[
              { label: 'Rouge', color: 'bg-red-600 hover:bg-red-700 text-white' },
              { label: 'Rose', color: 'bg-pink-400 hover:bg-pink-500 text-white' },
              { label: 'Blanc', color: 'bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300' },
            ].map(opt => (
              <button key={opt.label} data-testid={`wine-${opt.label.toLowerCase()}`}
                onClick={() => {
                  if (pendingWineItem) {
                    const modified = { ...pendingWineItem, name: `${pendingWineItem.name} - ${opt.label}` };
                    addToCartDirect(modified, null, null, []);
                    // Show drinks-with-food prompt
                    const hasFood = cart.some(i => i.type !== 'boisson');
                    if (hasFood && !drinksWithFood && !showDrinksPrompt) {
                      setShowDrinksPrompt(true);
                    }
                  }
                  setShowWinePicker(false);
                  setPendingWineItem(null);
                }}
                className={`py-3 rounded-lg font-bold text-sm transition-all ${opt.color}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
        </div>
      )}
    </div>
  );
};

export default CustomerOrder;
